// The restaurant's Menu list (docs/plans/menu-list.md): the Orders tab's list style, with
// what a menu needs (category, kitchen or bar, veg marks, recipes) in place of stock
// columns. The whole menu is read once and filtered here; a menu is a few hundred items
// at most. Staff mark a dish "Sold out today" here; it comes back by itself the next
// morning (lib/sold-out.ts, docs/plans/menu-sections.md).
import type { ProductRow } from '@ims/data'
import {
  Badge,
  Button,
  buttonClass,
  cx,
  EmptyState,
  FilterPill,
  FilterPills,
  IconTile,
  Kbd,
  Pagination,
  SearchInput,
  Segmented,
  SkeletonTable,
  useToast,
} from '@ims/ui'
import {
  Ban,
  BookOpenText,
  CookingPot,
  Pencil,
  Pepper,
  RotateCcw,
  Upload,
  Wine,
} from '@ims/ui/icons'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { useLookups } from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import { money, qty } from '../lib/format.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { usePaged } from '../lib/paging.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { updateOp } from '../lib/rows.ts'
import { isSoldOut, soldOutUntil } from '../lib/sold-out.ts'
import { useRepo } from '../workspace.tsx'
import { ProductPhoto, usePhotoUrls } from '../photos.tsx'

type Diet = NonNullable<ProductRow['diet']>
export type MenuShow = 'menu' | 'hidden' | 'recipe' | 'soldout'

const DIET_WORDS: Record<Diet, string> = { veg: 'Veg', non_veg: 'Non-veg', egg: 'Egg' }
const DIET_COLOUR: Record<Diet, string> = {
  veg: 'text-success',
  non_veg: 'text-danger',
  egg: 'text-warning',
}

/** The square with a dot that menus in Nepal print beside a dish (as on the guest's page). */
export function DietMark({
  diet,
  label = true,
  className,
}: {
  diet: Diet
  /** False beside the word itself (a filter button), so it is not read twice. */
  label?: boolean
  className?: string
}) {
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label ? DIET_WORDS[diet] : undefined}
      aria-hidden={label ? undefined : true}
      title={label ? DIET_WORDS[diet] : undefined}
      className={cx(
        'inline-grid size-3.5 shrink-0 place-items-center rounded-[3px] border-[1.5px] border-current',
        DIET_COLOUR[diet],
        className,
      )}
    >
      <span className="size-1.5 rounded-full bg-current" />
    </span>
  )
}

function Marks({ product }: { product: ProductRow }) {
  return (
    <>
      {product.diet && <DietMark diet={product.diet} />}
      {product.spicy && (
        <span role="img" aria-label="Spicy" title="Spicy" className="inline-grid text-danger">
          <Pepper className="size-[15px]" />
        </span>
      )}
    </>
  )
}

interface MenuData {
  /** What is on the menu or could be: sold products that are not variants. */
  items: ProductRow[]
  /** Each group's variants. */
  variants: Map<string, ProductRow[]>
  /** Products with a recipe. */
  withRecipe: Set<string>
  /** Stock across all locations, by product. */
  stock: Map<string, number>
}

function useMenu(): MenuData | undefined {
  return useLive(
    async (repo) => {
      const [sold, recipes, levels] = await Promise.all([
        repo.list('products', { where: { for_sale: true }, limit: 1_000_000 }),
        repo.list('product_components', { limit: 1_000_000 }),
        repo.stock.levels(),
      ])
      const items: ProductRow[] = []
      const variants = new Map<string, ProductRow[]>()
      for (const p of sold.rows) {
        if (p.kind !== 'variant' || !p.parent_id) items.push(p)
        else variants.set(p.parent_id, [...(variants.get(p.parent_id) ?? []), p])
      }
      const stock = new Map<string, number>()
      for (const l of levels) stock.set(l.product_id, (stock.get(l.product_id) ?? 0) + l.qty_milli)
      return {
        items,
        variants,
        withRecipe: new Set(recipes.rows.map((r) => r.product_id)),
        stock,
      }
    },
    [],
    ['products', 'product_components', 'stock_movements'],
  ).data
}

/** The menu's counts for the header line: total, categories, hidden, and dishes with no recipe. */
export interface MenuCounts {
  items: number
  categories: number
  hidden: number
  noRecipe: number
  /** On the menu and sold out today. */
  soldOut: number
}

// One row: from 640px a row of the table; below that the same cells make a card.
const COLS =
  'sm:grid-cols-[minmax(0,2.1fr)_minmax(0,0.95fr)_minmax(0,0.7fr)_minmax(0,0.95fr)_minmax(0,0.75fr)_17rem]'
const ROW = cx(
  'grid items-center gap-x-3 gap-y-1 max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:rounded-xl max-sm:border max-sm:border-border max-sm:bg-surface max-sm:p-3 max-sm:shadow-card',
  'sm:border-b sm:border-border/60 sm:px-4 sm:py-2',
  COLS,
)
const CELL = {
  name: 'flex min-w-0 items-center gap-2.5 max-sm:col-start-1 max-sm:row-span-2 max-sm:row-start-1',
  category: 'text-sm max-sm:hidden',
  station: 'flex items-center gap-1.5 text-[13px] text-muted max-sm:hidden',
  status: 'max-sm:col-start-2 max-sm:row-start-2 max-sm:self-start max-sm:justify-self-end',
  price: 'tabular text-right font-bold max-sm:col-start-2 max-sm:row-start-1 max-sm:self-end',
  actions:
    // On a phone only "Add recipe" shows here, so a row without it has no button line.
    'flex justify-end gap-1.5 max-sm:col-span-2 max-sm:row-start-3 max-sm:mt-1.5 max-sm:[&:not(:has(a,[data-phone]))]:hidden',
}
const selectedRow = 'lg:bg-sell-wash lg:shadow-[inset_3px_0_0_var(--color-primary)]'

export function MenuList({
  initialCategory,
  show,
  onShow,
  search,
  onSearch,
  searchEnd,
  canEdit,
  selecting,
  selected,
  onToggle,
  onEdit,
  onAdd,
  onCounts,
}: {
  /** A category to start on (a Categories row links here with `?category=`). */
  initialCategory?: string | null
  /** Which items: on the menu, hidden, dishes with no recipe, or sold out today. */
  show: MenuShow
  onShow: (show: MenuShow) => void
  search: string
  onSearch: (value: string) => void
  /** The scanner button inside the search box. */
  searchEnd: ReactNode
  canEdit: boolean
  /** Bulk edit: a tick box on each row. */
  selecting: boolean
  selected: ReadonlySet<string>
  onToggle: (ids: string[], on: boolean) => void
  onEdit: (product: ProductRow) => void
  onAdd: () => void
  /** The header's line of counts, once the menu has loaded. */
  onCounts: (counts: MenuCounts) => void
}) {
  const settings = useSettings()
  const lookups = useLookups()
  const navigate = useNavigate()
  const openItem = useOpen()
  const menu = useMenu()
  const repo = useRepo()
  const toast = useToast()
  const [category, setCategory] = useState<string>(initialCategory ?? 'all')
  const [marking, setMarking] = useState(false)
  const [diet, setDiet] = useState<Diet | null>(null)
  const [picked, setPicked] = useState<string | null>(null)

  const items = menu?.items ?? []
  const isGroup = (p: ProductRow) => p.kind === 'group'
  const noRecipe = (p: ProductRow) =>
    !!menu && p.is_active && !isGroup(p) && !p.track_stock && !menu.withRecipe.has(p.id)
  const now = new Date()
  const soldOut = (p: ProductRow) => p.is_active && !isGroup(p) && isSoldOut(p, now)
  const categoryOf = (p: ProductRow) =>
    p.category_id ? lookups.categories.find((c) => c.id === p.category_id) : undefined

  // The header's counts follow the menu, not the filters.
  const counts: MenuCounts = {
    items: items.length,
    categories: new Set(items.map((p) => p.category_id).filter(Boolean)).size,
    hidden: items.filter((p) => !p.is_active).length,
    noRecipe: items.filter(noRecipe).length,
    soldOut: items.filter(soldOut).length,
  }
  const countsKey = JSON.stringify(counts)
  useEffect(() => {
    if (menu) onCounts(JSON.parse(countsKey) as MenuCounts)
    // `countsKey` stands for the counts; the callback is the parent's setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countsKey, !!menu])

  const term = search.trim().toLowerCase()
  const matches = (p: ProductRow) =>
    !term ||
    [p.name, p.sku, p.barcode, p.tags, lookups.brand(p.brand_id)?.name].some((v) =>
      v?.toLowerCase().includes(term),
    )
  const inView = items.filter((p) =>
    show === 'hidden'
      ? !p.is_active
      : show === 'recipe'
        ? noRecipe(p)
        : show === 'soldout'
          ? soldOut(p)
          : p.is_active,
  )
  const inDiet = inView.filter((p) => (diet ? p.diet === diet : true) && matches(p))
  // Sections in the guest menu's order, then by name; items with no section come last.
  const sections = lookups.categories
    .filter((c) => inDiet.some((p) => p.category_id === c.id))
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
  const rank = new Map(sections.map((c, i) => [c.id, i]))
  const shown = inDiet
    .filter((p) => category === 'all' || p.category_id === category)
    .sort(
      (a, b) =>
        (rank.get(a.category_id ?? '') ?? sections.length) -
          (rank.get(b.category_id ?? '') ?? sections.length) || a.name.localeCompare(b.name),
    )
  // 25 a page; a new search or filter starts at the first page.
  const paged = usePaged(shown, JSON.stringify([show, category, diet, term]))
  const pageRows = paged.rows
  const selectedItem = pageRows.find((p) => p.id === picked) ?? pageRows[0]

  const rows = useRef<HTMLUListElement>(null)
  useListKeys({
    ids: pageRows.map((p) => p.id),
    active: selectedItem?.id ?? null,
    setActive: setPicked,
    page: paged,
    list: rows,
    open: () => selectedItem && openItem(`/products/${selectedItem.id}`),
    keys: {
      e: () => canEdit && selectedItem && onEdit(selectedItem),
      r: () => selectedItem && !isGroup(selectedItem) && navigate(`/products/${selectedItem.id}`),
    },
  })

  const photos = usePhotoUrls(pageRows.map((p) => p.image_path))

  /** Sold out until the next morning, or back on; `Undo` puts the old value back. */
  const markSoldOut = async (list: ProductRow[], out: boolean) => {
    if (list.length === 0) return
    setMarking(true)
    try {
      const until = out ? soldOutUntil() : null
      await repo.commit(list.map((p) => updateOp('products', p, { sold_out_until: until })))
      const one = list.length === 1 ? list[0]!.name : `${list.length} items`
      toast.success(
        out
          ? `${one} is sold out today`
          : `${one} ${list.length === 1 ? 'is' : 'are'} back on the menu`,
        {
          detail: out ? 'Back on the menu at 5:00 tomorrow morning.' : undefined,
          action: {
            label: 'Undo',
            onClick: () =>
              void repo.commit(
                list.map((p) => updateOp('products', p, { sold_out_until: p.sold_out_until })),
              ),
          },
        },
      )
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setMarking(false)
    }
  }
  const soldOutItems = items.filter(soldOut)

  const status = (p: ProductRow): ReactNode => {
    if (!p.is_active) return <Badge>Hidden</Badge>
    if (soldOut(p)) return <Badge tone="warning">Sold out today</Badge>
    const tracked = isGroup(p)
      ? (menu?.variants.get(p.id) ?? []).some((v) => v.track_stock)
      : p.track_stock
    if (!tracked) return <span className="text-[13px] text-muted max-sm:hidden">Made to order</span>
    const left = isGroup(p)
      ? (menu?.variants.get(p.id) ?? []).reduce((n, v) => n + (menu?.stock.get(v.id) ?? 0), 0)
      : (menu?.stock.get(p.id) ?? 0)
    if (left <= 0) return <Badge tone="danger">Out of stock</Badge>
    const low = !isGroup(p) && p.min_stock_milli > 0 && left <= p.min_stock_milli
    return <Badge tone={low ? 'warning' : 'success'}>{qty(left)} left</Badge>
  }

  const price = (p: ProductRow) => {
    const prices = isGroup(p)
      ? (menu?.variants.get(p.id) ?? []).map((v) => v.sell_price_paisa ?? p.sell_price_paisa ?? 0)
      : [p.sell_price_paisa ?? 0]
    const low = Math.min(...prices, Infinity)
    const high = Math.max(...prices, -Infinity)
    if (!Number.isFinite(low)) return '—'
    return low === high ? money(low, settings) : `${money(low, settings)}–${money(high, settings)}`
  }

  const allShownSelected = shown.length > 0 && shown.every((p) => selected.has(p.id))

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <Segmented
          label="Which items"
          value={show}
          onChange={(v) => {
            onShow(v)
            setCategory('all')
          }}
          options={[
            { value: 'menu', label: 'On the menu' },
            { value: 'hidden', label: 'Hidden' },
            { value: 'recipe', label: 'No recipe' },
            ...(counts.soldOut > 0 || show === 'soldout'
              ? [{ value: 'soldout' as const, label: `Sold out today ${counts.soldOut}` }]
              : []),
          ]}
          className="max-sm:w-full"
        />
        <div role="group" aria-label="Veg or non-veg" className="flex gap-1.5">
          {(['veg', 'non_veg', 'egg'] as const).map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={diet === d}
              onClick={() => setDiet(diet === d ? null : d)}
              className={cx(
                'flex h-9 items-center gap-2 rounded-xl border px-3 text-sm whitespace-nowrap transition-colors',
                diet === d
                  ? 'border-primary/40 bg-primary-light font-semibold text-primary'
                  : 'border-border bg-surface font-medium text-muted hover:bg-soft hover:text-ink',
              )}
            >
              <DietMark diet={d} label={false} />
              {DIET_WORDS[d]}
            </button>
          ))}
        </div>
        <SearchInput
          listKeys
          value={search}
          onChange={onSearch}
          placeholder="Name or code"
          end={searchEnd}
          className="w-full sm:ml-auto sm:w-64"
        />
      </div>
      <FilterPills label="Category">
        <FilterPill selected={category === 'all'} onSelect={() => setCategory('all')}>
          All {inDiet.length}
        </FilterPill>
        {sections.map((c) => (
          <FilterPill key={c.id} selected={category === c.id} onSelect={() => setCategory(c.id)}>
            {c.name} {inDiet.filter((p) => p.category_id === c.id).length}
          </FilterPill>
        ))}
      </FilterPills>

      {soldOutItems.length > 0 && show !== 'hidden' && (
        <div
          role="note"
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-warning-light px-4 py-3 text-sm text-warning"
        >
          <Ban className="size-5 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">
            <b>
              {soldOutItems.length === 1
                ? '1 item is sold out today:'
                : `${soldOutItems.length} items are sold out today:`}
            </b>{' '}
            {soldOutItems.map((p) => p.name).join(', ')}. Waiters can’t add{' '}
            {soldOutItems.length === 1 ? 'it' : 'them'} and the QR menu shows{' '}
            {soldOutItems.length === 1 ? 'it' : 'them'} greyed. Back by{' '}
            {soldOutItems.length === 1 ? 'itself' : 'themselves'} tomorrow morning.
          </span>
          {canEdit && (
            <Button
              size="sm"
              variant="secondary"
              loading={marking}
              className="h-[34px] text-[13px]"
              onClick={() => void markSoldOut(soldOutItems, false)}
            >
              Put all back on
            </Button>
          )}
        </div>
      )}

      {!menu ? (
        <div className="rounded-card bg-surface shadow-card">
          <SkeletonTable cols={5} />
        </div>
      ) : shown.length === 0 ? (
        <div className="rounded-card bg-surface shadow-card">
          <EmptyState
            icon={<BookOpenText className="size-6" />}
            title={
              items.length === 0
                ? 'The menu is empty'
                : show === 'recipe' && !term && !diet
                  ? 'Every dish has a recipe'
                  : show === 'hidden' && !term && !diet
                    ? 'Nothing is hidden'
                    : show === 'soldout' && !term && !diet
                      ? 'Nothing is sold out'
                      : 'No item matches'
            }
            description={
              items.length === 0
                ? 'Add your dishes and drinks, or bring in your whole list from Excel in one go.'
                : undefined
            }
            action={
              items.length === 0 &&
              canEdit && (
                <div className="flex flex-wrap justify-center gap-2">
                  <Button onClick={onAdd}>Add item</Button>
                  <Button
                    variant="secondary"
                    icon={<Upload className="size-4" />}
                    onClick={() => navigate('/products/import')}
                  >
                    Import from Excel
                  </Button>
                </div>
              )
            }
          />
        </div>
      ) : (
        <section
          aria-label="Menu"
          className="min-w-0 sm:overflow-hidden sm:rounded-card sm:bg-surface sm:shadow-card"
        >
          <div
            aria-hidden={!selecting}
            className={cx(
              'grid items-center gap-x-3 border-b border-border px-4 py-2.5 text-table-head text-subtle max-sm:hidden',
              COLS,
            )}
          >
            <span className="flex items-center gap-2.5">
              {selecting && (
                <input
                  type="checkbox"
                  aria-label="Select all shown"
                  className="size-4 accent-primary"
                  checked={allShownSelected}
                  onChange={(e) =>
                    onToggle(
                      shown.map((p) => p.id),
                      e.target.checked,
                    )
                  }
                />
              )}
              Item
            </span>
            <span>Category</span>
            <span>Goes to</span>
            <span>Status</span>
            <span className="text-right">Price</span>
            <span />
          </div>
          <ul ref={rows} className="max-sm:space-y-2.5">
            {pageRows.map((p) => {
              const on = selectedItem?.id === p.id
              const variants = menu.variants.get(p.id)
              const brand = lookups.brand(p.brand_id)?.name
              const note = variants
                ? `${variants.length} variants`
                : p.description?.trim() || brand || ''
              const missing = noRecipe(p)
              const station =
                p.kitchen_station === 'bar'
                  ? 'Bar'
                  : p.kitchen_station === 'none'
                    ? 'No ticket'
                    : 'Kitchen'
              const photo = p.image_path ? photos[p.image_path] : undefined
              return (
                <li
                  key={p.id}
                  data-id={p.id}
                  aria-current={on ? 'true' : undefined}
                  onClick={() => openItem(`/products/${p.id}`)}
                  className={cx(ROW, 'cursor-pointer', on ? selectedRow : 'lg:hover:bg-soft')}
                >
                  <span className={CELL.name}>
                    {selecting && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${p.name}`}
                        className="size-4 shrink-0 accent-primary"
                        checked={selected.has(p.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => onToggle([p.id], e.target.checked)}
                      />
                    )}
                    {photo ? (
                      <ProductPhoto
                        url={photo}
                        alt=""
                        className="size-9 shrink-0 rounded-xl object-cover max-sm:size-11"
                      />
                    ) : (
                      <IconTile area="stock" size="sm" className="max-sm:size-11">
                        <BookOpenText className="size-4" />
                      </IconTile>
                    )}
                    <span className="min-w-0">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <Link
                          to={`/products/${p.id}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            moveClick(`/products/${p.id}`, 'open')(e)
                          }}
                          className={cx(
                            'font-bold hover:underline focus-visible:underline max-sm:line-clamp-2 sm:truncate',
                            !p.is_active && 'text-muted',
                          )}
                        >
                          {p.name}
                        </Link>
                        <Marks product={p} />
                      </span>
                      <span className="block truncate text-xs text-muted sm:text-[12.5px]">
                        <span className="sm:hidden">
                          {[categoryOf(p)?.name ?? 'No category', station].join(' · ')}
                        </span>
                        <span className="max-sm:hidden">
                          {note}
                          {note && missing && ' · '}
                          {missing && (
                            <span className="font-semibold text-warning">No recipe yet</span>
                          )}
                        </span>
                      </span>
                    </span>
                  </span>
                  <span className={CELL.category}>
                    {categoryOf(p)?.name ?? <span className="text-muted">No category</span>}
                  </span>
                  <span className={CELL.station}>
                    {station === 'Bar' ? (
                      <Wine className="size-[15px] text-subtle" aria-hidden />
                    ) : station === 'Kitchen' ? (
                      <CookingPot className="size-[15px] text-subtle" aria-hidden />
                    ) : null}
                    {station}
                  </span>
                  <span className={CELL.status}>{status(p)}</span>
                  <span className={CELL.price}>{price(p)}</span>
                  <span className={CELL.actions} onClick={(e) => e.stopPropagation()}>
                    {missing && canEdit && (
                      <Link
                        to={`/products/${p.id}`}
                        aria-label={`Add a recipe to ${p.name}`}
                        className={buttonClass({
                          variant: 'soft',
                          size: 'sm',
                          className:
                            'h-[34px] px-3 text-[13px] whitespace-nowrap max-sm:h-10 max-sm:flex-1',
                        })}
                      >
                        <CookingPot className="size-4" aria-hidden />
                        Add recipe
                      </Link>
                    )}
                    {canEdit && p.is_active && !isGroup(p) && (
                      <Button
                        data-phone
                        variant={soldOut(p) ? 'soft' : 'secondary'}
                        size="sm"
                        aria-label={
                          soldOut(p)
                            ? `Put ${p.name} back on the menu`
                            : `Sold out today: ${p.name}`
                        }
                        className="h-[34px] px-2.5 text-[13px] whitespace-nowrap max-sm:h-10 max-sm:flex-1"
                        icon={
                          soldOut(p) ? <RotateCcw className="size-4" /> : <Ban className="size-4" />
                        }
                        disabled={marking}
                        onClick={() => void markSoldOut([p], !soldOut(p))}
                      >
                        {soldOut(p) ? 'Back on' : 'Sold out'}
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Edit ${p.name}`}
                        title="Edit"
                        className="h-[34px] w-[34px] px-0 max-sm:hidden"
                        icon={<Pencil className="size-4" />}
                        onClick={() => onEdit(p)}
                      />
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
            <span>Sorted by category, then name.</span>
            <span className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 max-lg:hidden">
              <span>
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move
              </span>
              <span>
                <Kbd>Enter</Kbd> open
              </span>
              {canEdit && (
                <span>
                  <Kbd>E</Kbd> edit
                </span>
              )}
              <span>
                <Kbd>R</Kbd> recipe
              </span>
            </span>
          </div>
          <Pagination
            offset={paged.offset}
            limit={paged.limit}
            total={paged.total}
            onChange={paged.setOffset}
          />
        </section>
      )}
    </div>
  )
}
